import { useOrderRouteLog } from '../../../../api/useFreight/useOrderRouteLog'
import { useTransactions } from '../../../../api/useFreight/useTransactions'
import { useOrders } from '../../../../api/useOrders'
import type { OrderFreightData } from '../types'

/**
 * Props for the useOrderFreightData hook
 */
interface UseOrderFreightDataProps {
  /** The order ID to fetch data for */
  orderId?: string
  /** The order number to fetch transactions for */
  orderNumber?: string
  /** Whether to show cost or revenue transactions */
  costRevenue: 'Cost' | 'Revenue'
  /** The invoice group number if available */
  invoiceGroupNumber?: string | null
}

/**
 * Hook to fetch and manage the data needed for the OrderFreightDrawer component
 * Fetches order details, route logs, and transactions
 */
export function useOrderFreightData({
  orderId,
  orderNumber,
  costRevenue,
  invoiceGroupNumber,
}: UseOrderFreightDataProps): OrderFreightData {
  const { useOrderRouteLogQuery } = useOrderRouteLog()
  const { useOrderByIdQuery } = useOrders()
  const { useTransactionByOrderQuery } = useTransactions()

  // Fetch order data
  const { data: orderExtraData, isLoading: isLoadingOrder } = useOrderByIdQuery(orderId)

  // Fetch route log data
  const { data: routeLogData, isPending: isLoadingRoute } = useOrderRouteLogQuery(orderId)

  // Fetch transaction data
  const { data: transactionDetails, isFetching: transactionsLoading } = useTransactionByOrderQuery({
    orderNumber,
    costRevenue,
  })

  // Determine the invoice ID if available
  const invoiceId = invoiceGroupNumber !== undefined ? (invoiceGroupNumber as string) : undefined

  return {
    orderExtraData,
    isLoadingOrder,
    routeLogData,
    isLoadingRoute,
    transactionDetails,
    transactionsLoading,
    invoiceId,
  }
}
